import { fontWeightBold } from '@ui-components/design-tokens';
import { PlayBadge, PlayBadgeTheme } from '@ui-components/PlayBadge';
import { ImageRatio } from '@ui-components/PlayImage';
import {
    ContentItemFragment,
    NextToPlayFragment,
    Presentation,
} from 'app/graphql/__generated__/types/contento';
import placeHolderImage from 'public/static//images/placeholders/16_9.png';
import React from 'react';
import {
    createImageProxyTemplate,
    formatImageProxyUrl,
    formatUrl,
    isImageProxyTemplate,
} from '../../../../utils/svtimage';
import EditMode from '../EditMode/EditMode';
import {
    OutsideSwedenContainer,
    OutsideSwedenText,
    StyledContentItemFigure,
    StyledPlayIcon,
} from './ContentItemFigure.styles';

type Props = {
    content: ContentItemFragment | NextToPlayFragment;
    defaultImage?: string;
    showPlaceholder?: boolean;
    presentation?: Presentation;
    editModeActivated?: boolean;
    markedAsDeleted?: boolean;
    autoPlayOverlay?: boolean;
    playingNow?: boolean;
    children?: JSX.Element;
};

const ContentItemFigure = ({
    content,
    defaultImage,
    showPlaceholder,
    presentation,
    editModeActivated,
    markedAsDeleted,
    autoPlayOverlay,
    playingNow,
    children,
}: Props): JSX.Element => {
    const onlyAvailableInSweden = content.item?.restrictions?.onlyAvailableInSweden;

    let id: string;
    let changed: number;
    let imageFormat: string;
    let imageRatio: ImageRatio;
    let imageRatioType: ImageRatio;
    let altText: string;

    if (presentation === Presentation.PortraitPosters) {
        const resolvedImage = content.images.portrait;

        id = resolvedImage.id;
        changed = resolvedImage.changed;
        altText = resolvedImage.description;
        imageFormat = 'twothree';
        imageRatioType = ImageRatio.TWO_BY_THREE;
    } else {
        const resolvedImage = content.images.wide;

        id = resolvedImage.id;
        changed = resolvedImage.changed;
        altText = resolvedImage.description;
        imageFormat = 'wide';
    }

    const imageTemplate = createImageProxyTemplate(id, changed);

    let src: string;
    if (isImageProxyTemplate(imageTemplate)) {
        src = imageTemplate ? formatImageProxyUrl(imageTemplate, imageFormat, 480, imageRatio) : defaultImage;
    } else {
        src = imageTemplate ? formatUrl(imageTemplate, 'medium') : defaultImage;
    }

    const opacity = editModeActivated ? 0.1 : undefined;

    return (
        <StyledContentItemFigure
            src={showPlaceholder || !src ? placeHolderImage : src}
            alt={altText}
            imageRatio={imageRatioType}
            blurred={opacity ? false : onlyAvailableInSweden}
            showPlaceholder={showPlaceholder}
            opacity={opacity}
        >
            <>
                {autoPlayOverlay && <StyledPlayIcon onlyAvailableInSweden={onlyAvailableInSweden} />}
                {!playingNow && content.badge && !editModeActivated && (
                    <PlayBadge
                        text={content?.badge?.text}
                        theme={content?.badge?.type}
                        accessibilityText={content?.badge?.altText}
                        overlay
                    />
                )}
                {playingNow && (
                    <PlayBadge
                        text="Spelas nu"
                        theme={PlayBadgeTheme.PLAYING_NOW}
                        accessibilityText="Spelas nu"
                        overlay
                    />
                )}
                {editModeActivated && (
                    <EditMode markedAsDeleted={markedAsDeleted} programTitle={content.heading} />
                )}
                {!editModeActivated && onlyAvailableInSweden && (
                    <OutsideSwedenContainer>
                        <OutsideSwedenText weight={fontWeightBold}>Kan bara ses i Sverige</OutsideSwedenText>
                    </OutsideSwedenContainer>
                )}

                {children}
            </>
        </StyledContentItemFigure>
    );
};

export default ContentItemFigure;
